/**
 * Layout Controls Component
 * Controls for customizing graph layout and view settings
 */

import React from 'react';
import { Maximize, ZoomIn, ZoomOut, Map, ArrowDownToLine, ArrowRightToLine } from 'lucide-react';
import { Button } from '../common/Button';

export interface LayoutOptions {
  direction: 'TB' | 'LR';
  nodeSpacing: number;
  rankSpacing: number;
  showMiniMap: boolean;
}

interface LayoutControlsProps {
  options: LayoutOptions;
  onOptionsChange: (options: LayoutOptions) => void;
  onFitView: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
}

export const LayoutControls: React.FC<LayoutControlsProps> = ({
  options,
  onOptionsChange,
  onFitView,
  onZoomIn,
  onZoomOut,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  return (
    <div className="absolute top-4 right-4 z-30 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
      {/* Quick Actions - Always Visible */}
      <div className="flex items-center gap-1 p-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onFitView}
          className="p-2"
          title="Fit View"
        >
          <Maximize className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onZoomIn}
          className="p-2"
          title="Zoom In"
        >
          <ZoomIn className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onZoomOut}
          className="p-2"
          title="Zoom Out"
        >
          <ZoomOut className="w-4 h-4" />
        </Button>
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <Button
          variant={isExpanded ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-2"
          title="Layout Settings"
        >
          <Map className="w-4 h-4" />
        </Button>
      </div>

      {/* Expanded Settings */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-4 space-y-4 w-72">
          {/* Direction Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Layout Direction
            </label>
            <div className="flex flex-col gap-2">
              <Button
                variant={options.direction === 'TB' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => onOptionsChange({ ...options, direction: 'TB' })}
                className="w-full flex items-center justify-center gap-2"
              >
                <ArrowDownToLine className="w-4 h-4" />
                Top to Bottom
              </Button>
              <Button
                variant={options.direction === 'LR' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => onOptionsChange({ ...options, direction: 'LR' })}
                className="w-full flex items-center justify-center gap-2"
              >
                <ArrowRightToLine className="w-4 h-4" />
                Left to Right
              </Button>
            </div>
          </div>

          {/* Node Spacing */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Node Spacing: {options.nodeSpacing}px
            </label>
            <input
              type="range"
              min="20"
              max="150"
              step="10"
              value={options.nodeSpacing}
              onChange={(e) => onOptionsChange({ ...options, nodeSpacing: parseInt(e.target.value) })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Compact</span>
              <span>Spacious</span>
            </div>
          </div>

          {/* Rank Spacing */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Level Spacing: {options.rankSpacing}px
            </label>
            <input
              type="range"
              min="60"
              max="200"
              step="10"
              value={options.rankSpacing}
              onChange={(e) => onOptionsChange({ ...options, rankSpacing: parseInt(e.target.value) })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Tight</span>
              <span>Wide</span>
            </div>
          </div>

          {/* Mini Map Toggle */}
          <div className="pt-2 border-t border-gray-200">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={options.showMiniMap}
                onChange={(e) => onOptionsChange({ ...options, showMiniMap: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Show Mini Map</span>
            </label>
          </div>

          {/* Reset to Defaults */}
          <div className="pt-2 border-t border-gray-200">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onOptionsChange({
                  direction: 'TB',
                  nodeSpacing: 50,
                  rankSpacing: 120,
                  showMiniMap: true,
                });
              }}
              className="w-full"
            >
              Reset to Defaults
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
